import React, { useEffect, useState } from 'react';
import './ThemePicker.css';

export type PaletteTheme = 'sun' | 'aurora' | 'mint' | 'midnight' | 'fog';

const STORAGE_KEY = 'fcyx_palette_theme';
const VALID_THEMES: PaletteTheme[] = ['sun', 'aurora', 'mint', 'midnight', 'fog'];
const THEMES: { id: PaletteTheme; label: string; gradient: string }[] = [
  { id: 'sun', label: 'Лето', gradient: 'linear-gradient(135deg,#2F6BFF,#FF6A3D)' },
  { id: 'aurora', label: 'Аврора', gradient: 'linear-gradient(135deg,#7C3CFF,#FF4F93)' },
  { id: 'mint', label: 'Мята', gradient: 'linear-gradient(135deg,#008A65,#FF4F86)' },
  { id: 'midnight', label: 'Полночь', gradient: 'linear-gradient(135deg,#3B82F6,#22D3EE)' },
  { id: 'fog', label: 'Туман', gradient: 'linear-gradient(135deg,#5C6AC4,#D9A066)' }
];

function prefersDark(): boolean {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  } catch (e) {
    return false;
  }
}

function readStoredTheme(): PaletteTheme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (VALID_THEMES.includes(v as PaletteTheme)) return v as PaletteTheme;
  } catch (e) {
    /* ignore */
  }
  return prefersDark() ? 'aurora' : 'sun';
}

function applyTheme(theme: PaletteTheme) {
  document.documentElement.setAttribute('data-p', theme);
}

/** Инициализирует палитру как можно раньше (до первого рендера). */
export function initStoredTheme() {
  applyTheme(readStoredTheme());
}

/**
 * Переключатель палитры — показывается только в своём личном профиле (не глобально).
 */
export function ThemePicker() {
  const [theme, setTheme] = useState<PaletteTheme>(() => readStoredTheme());

  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      /* ignore */
    }
  }, [theme]);

  return (
    <div className="fc-theme-picker" role="radiogroup" aria-label="Цветовая схема">
      <span className="fc-theme-picker__label">Цвета</span>
      {THEMES.map((t) => (
        <button
          key={t.id}
          type="button"
          role="radio"
          aria-checked={theme === t.id}
          className={`fc-theme-picker__btn${theme === t.id ? ' fc-theme-picker__btn--on' : ''}`}
          onClick={() => setTheme(t.id)}
        >
          <i className="fc-theme-picker__dot" style={{ background: t.gradient }} />
          {t.label}
        </button>
      ))}
    </div>
  );
}
